import React, { useMemo } from 'react';
import Table from 'react-bootstrap/Table';
import type { StatsGranularity } from '../../services/types';
import { formatGermanNumber, formatPeriodLong } from '../../utils/statisticsFormat';
import { mergeSeriesRows } from './statSeries';
import type { StatSeries } from './statSeries';
import { CHART_HEIGHT } from './TimeSeriesChart';

/** Props der TimeSeriesTable-Komponente. */
interface TimeSeriesTableProps {
  /** ID-Praefix des zugehoerigen Charts (z. B. "chart-registrations"). */
  readonly chartId: string;
  /** Granularitaet (steuert die Perioden-Formatierung). */
  readonly granularity: StatsGranularity;
  /** Eine oder zwei Serien (gleiche Daten wie das Chart). */
  readonly series: readonly StatSeries[];
}

/**
 * Tabellarische Ansicht einer Chart-Zeitreihe (Periode | Wert je Serie).
 * Gleiche Hoehe wie das Chart, bei Bedarf vertikal scrollbar; Zahlen in
 * tabular-nums fuer buendige Spalten.
 */
function TimeSeriesTable({ chartId, granularity, series }: TimeSeriesTableProps): React.JSX.Element {
  const rows = useMemo(() => mergeSeriesRows(series), [series]);

  return (
    <div
      id={`${chartId}-table-wrapper`}
      className="table-responsive"
      style={{ maxHeight: CHART_HEIGHT, overflowY: 'auto' }}
    >
      <Table
        id={`${chartId}-table`}
        size="sm"
        striped
        className="mb-0 align-middle"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        <thead id={`${chartId}-table-head`}>
          <tr id={`${chartId}-table-head-row`}>
            <th id={`${chartId}-table-header-period`}>Periode</th>
            {series.map((singleSeries) => (
              <th
                key={singleSeries.seriesKey}
                id={`${chartId}-table-header-${singleSeries.seriesKey}`}
                className="text-end"
              >
                {singleSeries.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody id={`${chartId}-table-body`}>
          {rows.map((row) => (
            <tr key={row.periodStart} id={`${chartId}-table-row-${row.periodStart}`}>
              <td id={`${chartId}-table-cell-${row.periodStart}-period`}>
                {formatPeriodLong(row.periodStart, granularity)}
              </td>
              {series.map((singleSeries) => {
                const value = row[singleSeries.seriesKey];
                return (
                  <td
                    key={singleSeries.seriesKey}
                    id={`${chartId}-table-cell-${row.periodStart}-${singleSeries.seriesKey}`}
                    className="text-end"
                  >
                    {typeof value === 'number' ? formatGermanNumber(value) : '–'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export default TimeSeriesTable;
